import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('bs_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('bs_token');
      localStorage.removeItem('bs_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  updatePreferences: (data) => api.put('/auth/preferences', data),
};

export const projectAPI = {
  list: (params = {}) => api.get('/projects', { params }),
  listAllTags: () => api.get('/projects/tags/all'),
  get: (id) => api.get(`/projects/${id}`),
  create: (data) => api.post('/projects', data),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
  connectRepo: (data) => api.post('/projects/connect-repo', data),
  analyzeRepo: (id, data) => api.post(`/projects/${id}/analyze-repo`, data),
  addRequirement: (id, data) => api.post(`/projects/${id}/requirements`, data),
  generateRequirements: (id, data) => api.post(`/projects/${id}/requirements/generate`, data),
  updateRequirement: (id, reqId, data) => api.put(`/projects/${id}/requirements/${reqId}`, data),
  deleteRequirement: (id, reqId) => api.delete(`/projects/${id}/requirements/${reqId}`),
  savePhase: (data) => api.post(`/projects/${data.project_id}/save-phase`, data),
  saveCode: (id, data) => api.post(`/projects/${id}/save-code`, data),
  generateDoc: (id, data) => api.post(`/projects/${id}/generate-doc`, data),
  saveTechStack: (id, data) => api.post(`/projects/${id}/technology`, data),
  getTechStack: (id) => api.get(`/projects/${id}/technology`),
  connectSourceControl: (id, data) => api.post(`/projects/${id}/source-control`, data),
  getSourceControl: (id) => api.get(`/projects/${id}/source-control`),
  disconnectSourceControl: (id) => api.delete(`/projects/${id}/source-control`),
  updateSettings: (id, data) => api.put(`/projects/${id}/settings`, data),
  // Tasks
  createTask: (id, data) => api.post(`/projects/${id}/tasks`, data),
  listTasks: (id, params) => api.get(`/projects/${id}/tasks`, { params }),
  updateTask: (id, taskId, data) => api.put(`/projects/${id}/tasks/${taskId}`, data),
  deleteTask: (id, taskId) => api.delete(`/projects/${id}/tasks/${taskId}`),
  // Members
  addMember: (id, data) => api.post(`/projects/${id}/members`, data),
  listMembers: (id) => api.get(`/projects/${id}/members`),
  updateMemberRole: (id, memberId, role) => api.put(`/projects/${id}/members/${memberId}?role=${role}`),
  removeMember: (id, memberId) => api.delete(`/projects/${id}/members/${memberId}`),
  // Epics
  createEpic: (id, data) => api.post(`/projects/${id}/epics`, data),
  listEpics: (id) => api.get(`/projects/${id}/epics`),
  updateEpic: (id, epicId, data) => api.put(`/projects/${id}/epics/${epicId}`, data),
  deleteEpic: (id, epicId) => api.delete(`/projects/${id}/epics/${epicId}`),
  // Modules
  createModule: (id, data) => api.post(`/projects/${id}/modules`, data),
  listModules: (id) => api.get(`/projects/${id}/modules`),
  deleteModule: (id, modId) => api.delete(`/projects/${id}/modules/${modId}`),
  // Bugs
  createBug: (id, data) => api.post(`/projects/${id}/bugs`, data),
  listBugs: (id, params) => api.get(`/projects/${id}/bugs`, { params }),
  updateBug: (id, bugId, data) => api.put(`/projects/${id}/bugs/${bugId}`, data),
  deleteBug: (id, bugId) => api.delete(`/projects/${id}/bugs/${bugId}`),
  // Integrations
  connectJira: (id, data) => api.post(`/projects/${id}/integrations/jira`, data),
  getIntegrations: (id) => api.get(`/projects/${id}/integrations`),
  disconnectJira: (id) => api.delete(`/projects/${id}/integrations/jira`),
  // Change Requests
  getChangeRequests: (id) => api.get(`/projects/${id}/change-requests`),
  addChangeRequest: (id, data) => api.post(`/projects/${id}/change-requests`, data),
  updateChangeRequest: (id, crId, data) => api.put(`/projects/${id}/change-requests/${crId}`, data),
  deleteChangeRequest: (id, crId) => api.delete(`/projects/${id}/change-requests/${crId}`),
  // Showstoppers
  getShowstoppers: (id) => api.get(`/projects/${id}/showstoppers`),
  addShowstopper: (id, data) => api.post(`/projects/${id}/showstoppers`, data),
  updateShowstopper: (id, ssId, data) => api.put(`/projects/${id}/showstoppers/${ssId}`, data),
  deleteShowstopper: (id, ssId) => api.delete(`/projects/${id}/showstoppers/${ssId}`),
  // Release Notes
  getReleaseNotes: (id) => api.get(`/projects/${id}/release-notes`),
  addReleaseNote: (id, data) => api.post(`/projects/${id}/release-notes`, data),
  updateReleaseNote: (id, rnId, data) => api.put(`/projects/${id}/release-notes/${rnId}`, data),
  deleteReleaseNote: (id, rnId) => api.delete(`/projects/${id}/release-notes/${rnId}`),
  generateReleaseNotes: (id, data) => api.post(`/projects/${id}/release-notes/generate`, data),
  // Test Cases
  getTestCases: (id) => api.get(`/projects/${id}/test-cases`),
  addTestCase: (id, data) => api.post(`/projects/${id}/test-cases`, data),
  updateTestCase: (id, tcId, data) => api.put(`/projects/${id}/test-cases/${tcId}`, data),
  deleteTestCase: (id, tcId) => api.delete(`/projects/${id}/test-cases/${tcId}`),
  // New Features
  getNewFeatures: (id) => api.get(`/projects/${id}/features`),
  addNewFeature: (id, data) => api.post(`/projects/${id}/features`, data),
  updateNewFeature: (id, fId, data) => api.put(`/projects/${id}/features/${fId}`, data),
  deleteNewFeature: (id, fId) => api.delete(`/projects/${id}/features/${fId}`),
  // Sprints
  getSprints: (id) => api.get(`/projects/${id}/sprints`),
  addSprint: (id, data) => api.post(`/projects/${id}/sprints`, data),
  updateSprint: (id, sId, data) => api.put(`/projects/${id}/sprints/${sId}`, data),
  deleteSprint: (id, sId) => api.delete(`/projects/${id}/sprints/${sId}`),
  // Meetings
  getMeetings: (id) => api.get(`/projects/${id}/meetings`),
  addMeeting: (id, data) => api.post(`/projects/${id}/meetings`, data),
  updateMeeting: (id, mId, data) => api.put(`/projects/${id}/meetings/${mId}`, data),
  deleteMeeting: (id, mId) => api.delete(`/projects/${id}/meetings/${mId}`),
  analyzeMeeting: (id, data) => api.post(`/projects/${id}/meetings/analyze`, data),
  createTasksFromMeeting: (id, mId, data) => api.post(`/projects/${id}/meetings/${mId}/create-tasks`, data),
  createShowstoppersFromMeeting: (id, mId, data) => api.post(`/projects/${id}/meetings/${mId}/create-showstoppers`, data),
  // Executive Summary & Scope
  generateExecutiveSummary: (id, data = {}) => api.post(`/projects/${id}/executive-summary/generate`, data),
  generateProjectScope: (id, data = {}) => api.post(`/projects/${id}/scope/generate`, data),
  generateProjectScopeSection: (id, data = {}) => api.post(`/projects/${id}/scope/generate-section`, data),
  updateProjectScopeSection: (id, data = {}) => api.put(`/projects/${id}/scope/section`, data),
  // Architecture
  generateArchitectureDiagram: (id, data = {}) => api.post(`/projects/${id}/architecture-diagram/generate`, data),
  // Risks
  getRisks: (id) => api.get(`/projects/${id}/risks`),
  analyzeRisks: (id, data = {}) => api.post(`/projects/${id}/risks/analyze`, data),
};

export const dashboardAPI = {
  risks: () => api.get('/dashboard/risks'),
  reminders: () => api.get('/dashboard/reminders'),
};

export const userAPI = {
  quota: () => api.get('/me/quota'),
};

export const chatAPI = {
  send: (data) => api.post('/chat', data),
};

export const logsAPI = {
  list: () => api.get('/logs'),
  stats: () => api.get('/logs/stats'),
};

export const providersAPI = {
  listActive: () => api.get('/providers'),
};

export const tokensAPI = {
  packages: () => api.get('/token-packages'),
  checkout: (data) => api.post('/payments/checkout', data, { headers: { 'X-Origin': window.location.origin } }),
  status: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

export const adminAPI = {
  providers: () => api.get('/admin/providers'),
  createProvider: (data) => api.post('/admin/providers', data),
  updateProvider: (id, data) => api.put(`/admin/providers/${id}`, data),
  deleteProvider: (id) => api.delete(`/admin/providers/${id}`),
  rates: () => api.get('/admin/rates'),
  createRate: (data) => api.post('/admin/rates', data),
  updateRate: (id, data) => api.put(`/admin/rates/${id}`, data),
  deleteRate: (id) => api.delete(`/admin/rates/${id}`),
  users: () => api.get('/admin/users'),
  updateUserTokens: (id, amount) => api.put(`/admin/users/${id}/tokens?amount=${amount}`),
  logs: () => api.get('/admin/logs'),
  stats: () => api.get('/admin/stats'),
  // Quotas
  quotas: () => api.get('/admin/quotas'),
  setDefaultQuota: (quota) => api.put('/admin/quotas/default', { quota }),
  setUserQuota: (userId, quota) => api.put(`/admin/quotas/user/${userId}`, { quota }),
  // Time saved rates
  timeSavedRates: () => api.get('/admin/time-saved-rates'),
  setTimeSavedRates: (rates) => api.put('/admin/time-saved-rates', { rates }),
  // Analytics & cross-tenant
  analytics: () => api.get('/admin/analytics'),
  allProjects: () => api.get('/admin/projects/all'),
  userActivity: (userId) => api.get(`/admin/users/${userId}/activity`),
};

export default api;
